import { useEffect, useState } from "react";
import DashboardShell, { StatCard } from "@/components/DashboardShell";
import { api, fmtNGN, formatErr } from "@/lib/api";
import { NIGERIA_STATES, STATE_LIST } from "@/lib/nigeria";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { toast } from "sonner";

const COLORS = ["#0A3622", "#F2B632", "#2D7A5D", "#B45309"];

export default function SchoolDashboard() {
  const [profile, setProfile] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ school_name: "", address: "", city: "", state: "", student_count_estimate: 0, bank_name: "", bank_code: "", bank_account_number: "", bank_account_name: "" });
  const [students, setStudents] = useState([]);
  const [txnData, setTxnData] = useState({ transactions: [], summary: { gross: 0, fees: 0, net: 0, count: 0 } });

  const reload = async () => {
    try {
      const p = await api.get("/schools/profile");
      setProfile(p.data?.id ? p.data : null);
      if (p.data?.id) setForm({ ...form, ...p.data });
      else setEdit(true);
      const s = await api.get("/schools/students"); setStudents(s.data);
      const t = await api.get("/schools/transactions"); setTxnData(t.data);
    } catch (e) { /* not fatal */ }
  };
  useEffect(() => { reload(); }, []); // eslint-disable-line

  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/schools/profile", form);
      setProfile(data); setEdit(false);
      toast.success("Profile saved");
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
  };

  const txns = txnData.transactions;
  const paid = txns.length;
  const totalStudents = students.length;
  const pieData = [
    { name: "Paid", value: paid },
    { name: "Pending", value: Math.max(totalStudents - paid, 0) },
  ];

  // Last 7 days bar
  const dayMap = {};
  for (let i = 6; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i);
    dayMap[d.toISOString().slice(0, 10)] = 0;
  }
  txns.forEach((t) => {
    const k = (t.created_at || "").slice(0, 10);
    if (k in dayMap) dayMap[k] += t.net_to_school || 0;
  });
  const barData = Object.entries(dayMap).map(([d, v]) => ({ day: d.slice(5), net: v }));

  if (edit) {
    return (
      <DashboardShell title={profile ? "Edit school profile" : "Set up your school"} subtitle="Tell us about your school to start collecting fees.">
        <form onSubmit={save} className="card max-w-2xl" data-testid="school-profile-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="School name" required><input className="input mt-1" required value={form.school_name} onChange={(e) => setForm({ ...form, school_name: e.target.value })} data-testid="school-name-input" /></Field>
            <Field label="Estimated students"><input type="number" className="input mt-1" value={form.student_count_estimate} onChange={(e) => setForm({ ...form, student_count_estimate: Number(e.target.value) })} data-testid="school-students-input" /></Field>
            <Field label="Address" required><input className="input mt-1" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="school-address-input" /></Field>
            <Field label="State" required>
              <select className="input mt-1" required value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value, city: "" })} data-testid="school-state-input">
                <option value="">Select a state…</option>
                {STATE_LIST.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="City" required>
              <select className="input mt-1" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} disabled={!form.state} data-testid="school-city-input">
                <option value="">{form.state ? "Select a city…" : "Select a state first"}</option>
                {form.state && (NIGERIA_STATES[form.state] || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>

          <div className="mt-7 pt-6 border-t border-[#E5E3DB]">
            <div className="overline mb-1">Disbursement bank account</div>
            <p className="text-xs text-[#4F5D4F] mb-4">Loans approved on Ascentremit are paid directly here in full — no platform fee on disbursement. Your collection account remains separate.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Bank name">
                <input className="input mt-1" value={form.bank_name || ""} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder="Guaranty Trust Bank" data-testid="school-bank-name-input" />
              </Field>
              <Field label="Bank code (3 digits)">
                <input className="input mt-1 font-mono" maxLength={3} value={form.bank_code || ""} onChange={(e) => setForm({ ...form, bank_code: e.target.value.replace(/\D/g, "") })} placeholder="058" data-testid="school-bank-code-input" />
              </Field>
              <Field label="Account number (NUBAN)">
                <input className="input mt-1 font-mono" maxLength={10} value={form.bank_account_number || ""} onChange={(e) => setForm({ ...form, bank_account_number: e.target.value.replace(/\D/g, "") })} placeholder="0123456789" data-testid="school-bank-account-input" />
              </Field>
              <Field label="Account name">
                <input className="input mt-1" value={form.bank_account_name || ""} onChange={(e) => setForm({ ...form, bank_account_name: e.target.value })} placeholder="As registered with the bank" data-testid="school-bank-name-input-2" />
              </Field>
            </div>
          </div>
          <div className="mt-6 flex gap-3">
            <button type="submit" className="btn-primary" data-testid="school-profile-save">Save profile</button>
            {profile && <button type="button" onClick={() => setEdit(false)} className="btn-ghost">Cancel</button>}
          </div>
        </form>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell title={profile?.school_name || "School"} subtitle={`${profile?.city || ""}, ${profile?.state || ""}`} actions={<button onClick={() => setEdit(true)} className="btn-outline !py-2 !px-4 text-sm" data-testid="edit-school-profile">Edit profile</button>}>
      <div className="grid lg:grid-cols-4 gap-6 mb-8">
        <StatCard label="Gross collected" value={fmtNGN(txnData.summary.gross)} sub={`${txnData.summary.count} transactions`} accent="primary" />
        <StatCard label="Platform fees" value={fmtNGN(txnData.summary.fees)} sub="Charged on collections" accent="warning" />
        <StatCard label="Net to you" value={fmtNGN(txnData.summary.net)} sub="Real-time settlement" accent="success" />
        <StatCard label="Students" value={totalStudents} sub={`Est. ${profile?.student_count_estimate || 0}`} accent="primary" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="card">
          <h3 className="font-display text-lg font-medium mb-4">Fee collection</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={pieData} dataKey="value" innerRadius={60} outerRadius={100} paddingAngle={4}>
                {pieData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex gap-6 justify-center text-sm mt-2">
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#0A3622]" />Paid · {paid}</span>
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#F2B632]" />Pending · {Math.max(totalStudents - paid, 0)}</span>
          </div>
        </div>
        <div className="card">
          <h3 className="font-display text-lg font-medium mb-4">Net settled · last 7 days</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={barData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E3DB" />
              <XAxis dataKey="day" stroke="#4F5D4F" fontSize={12} />
              <YAxis stroke="#4F5D4F" fontSize={12} />
              <Tooltip />
              <Bar dataKey="net" fill="#0A3622" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card">
        <h3 className="font-display text-lg font-medium mb-4">Recent collections</h3>
        {txns.length === 0 ? (
          <p className="text-sm text-[#4F5D4F] py-6 text-center">No collections yet.</p>
        ) : (
          <table className="dense">
            <thead><tr><th>Date</th><th>Reference</th><th>Channel</th><th>Gross</th><th>Fee</th><th>Net</th></tr></thead>
            <tbody>
              {txns.slice(0, 8).map((t) => (
                <tr key={t.id} data-testid={`school-txn-${t.id}`}>
                  <td>{new Date(t.created_at).toLocaleDateString()}</td>
                  <td className="font-mono text-xs">{t.reference}</td>
                  <td><span className="badge badge-info">{t.channel}</span></td>
                  <td className="font-mono">{fmtNGN(t.amount)}</td>
                  <td className="font-mono text-[#B45309]">-{fmtNGN(t.platform_fee)}</td>
                  <td className="font-mono font-medium text-[#2D7A5D]">{fmtNGN(t.net_to_school)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </DashboardShell>
  );
}

function Field({ label, required, children }) {
  return <label className="block"><span className="text-xs uppercase tracking-[0.14em] text-[#4F5D4F] font-semibold">{label}{required && <span className="text-[#F2B632]"> *</span>}</span>{children}</label>;
}
